import { AlertColor, useMediaQuery, useTheme } from '@mui/material';
import strings from 'common/Translation/Translate';
import { CountryCode } from 'libphonenumber-js';
import { FC, useCallback, useEffect, useState } from 'react';
import MasterDataEntityDrawer from '../CommonPage/MasterDataEntityDrawer';
import Notification from '../../../components/Notification';
import { FormDataModel } from '../../../components/Form';
import axiosInstance from '../../../utils/axios';
import { URLS } from '../../../utils/constants/urls';
import { fetchFromStorage } from '../../../utils/storage';
import { createDropdownData } from '../../../utils/validators/HelperFunctions';
import { EditProfileForm } from './EditProfileForm';

type EditProfileDrawerProps = {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
};

const EditProfileDrawer: FC<EditProfileDrawerProps> = ({ open, onClose, onSaved }) => {
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const drawerWidth = screenSizeDownSm ? '100%' : screenSizeDownMd ? '85%' : 720;
  const defaultCountry = (fetchFromStorage('defaultCountryCode') || 'IN') as CountryCode;
  const [formValues, setFormValues] = useState<FormDataModel>({});
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [languages, setLanguages] = useState<any>([]);
  const [countries, setCountries] = useState<any>([]);
  const [states, setStates] = useState<any>([]);
  const [cities, setCities] = useState<any>([]);
  const [salutations, setSalutations] = useState<any[]>([]);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: (error.response?.data?.error_description ?? error.response?.data?.message) ?? error.message,
    });
  };

  const getStateList = async (id: number) => {
    const { data } = await axiosInstance.get(URLS.states(id));
    setStates(data.map((item: any) => ({ label: item.name, value: item.id })));
  };

  const getCitiesList = async (id: number) => {
    const { data } = await axiosInstance.get(URLS.cities(id));
    setCities(data.map((item: any) => ({ label: item.name, value: item.id })));
  };

  const loadProfile = useCallback(async () => {
    try {
      setDrawerLoading(true);
      const [{ data: profile }, { data: languageData }, { data: countryData }, salutationsRes] = await Promise.all([
        axiosInstance.get(URLS.userProfile),
        axiosInstance.get(URLS.language),
        axiosInstance.get(URLS.countries),
        axiosInstance(URLS.getSalutations),
      ]);

      setLanguages(languageData.map((item: any) => ({ label: item.text, value: item.text })));
      setCountries(countryData.map((item: any) => ({ label: item.name, value: item.id })));
      setSalutations(createDropdownData(salutationsRes.data, ['id', 'name']));

      if (profile?.country?.value) {
        await getStateList(profile.country.value);
      } else {
        setStates([]);
      }
      if (profile?.state?.value) {
        await getCitiesList(profile.state.value);
      } else {
        setCities([]);
      }

      setFormValues({
        ...profile,
        language: profile?.language?.value,
        city: profile?.city?.value,
        state: profile?.state?.value,
        country: profile?.country?.value,
        phone: profile?.phone,
        title: profile?.title ? { label: profile.title, value: profile.title } : null,
      });
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setDrawerLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      void loadProfile();
    } else {
      setFormValues({});
      setStates([]);
      setCities([]);
    }
  }, [open, loadProfile]);

  const handleFieldChange = (key: string, value: any) => {
    if (key === 'country') {
      if (value) void getStateList(value);
      else setStates([]);
      setCities([]);
      setFormValues(prev => ({ ...prev, country: value, state: '', city: '' }));
      return;
    }
    if (key === 'state') {
      if (value) void getCitiesList(value);
      else setCities([]);
      setFormValues(prev => ({ ...prev, state: value, city: '' }));
      return;
    }
    setFormValues(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async (body: FormDataModel) => {
    try {
      setSaving(true);
      const { status } = await axiosInstance.put(URLS.userProfile, {
        ...body,
        pinCode: body.pinCode === '' ? null : body.pinCode,
        phone: body.phone,
        title: (body?.title as any)?.value ?? body?.title,
        company: body.companyName != null && body.companyName !== '',
      });
      if (status === 200) {
        setMessage({
          display: true,
          severity: 'success',
          message: strings.profile_reset_success_message,
        });
        onSaved?.();
        onClose();
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <MasterDataEntityDrawer
        open={open}
        title={strings.user_profile_edit_profile || 'Edit Profile'}
        isEdit
        width={drawerWidth}
        formModel={EditProfileForm(strings, salutations, languages, countries, states, cities, defaultCountry)}
        formValues={formValues}
        loading={drawerLoading}
        saving={saving}
        saveLabel={strings.userprofile_button_updateProfile || 'Update Profile'}
        onClose={onClose}
        onSave={handleSave}
        onFieldChange={handleFieldChange}
        onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}
      />
    </>
  );
};

export default EditProfileDrawer;
